export interface FilterOption {
    value: string;
    label: string;
}

export interface DistributionItem {
    locatorName: string;
    algorithm: string;
    bps: number;
    pct: number;
}

export interface TimeSeriesPoint {
    time: string;
    series: { locatorName: string; mbps: number }[];
}

export interface LatencyRouter {
    router: string;
    avgDelay: number;
    minDelay: number;
    maxDelay: number;
}

export interface LatencyPoint {
    time: string;
    routers: LatencyRouter[];
}

export interface LinkSnapshot {
    source: string;
    interface: string;
    neighbor: string | null;
    delay: number;
    minDelay: number;
    maxDelay: number;
    jitter: number;
    bandwidthKbps: number;
    packetLoss: number;
    algos: string[];
}

export interface LinkHealthTimePoint {
    time: string;
    links: Record<string, { delay: number; jitter: number; loss: number; min: number; max: number }>;
}

export interface LinkHealthData {
    source: string;
    timeRange: string;
    interval: string;
    links: string[];
    timeSeries: LinkHealthTimePoint[];
    snapshot: LinkSnapshot[];
}

export interface HeatmapData {
    routers: string[];
    algorithms: string[];
    matrix: number[][];
}

export interface AlertItem {
    type: string;
    severity: string;
    resource: string;
    message: string;
    values: Record<string, number>;
    detailLink: string;
}

export interface AlertsData {
    summary: Record<string, number>;
    alerts: AlertItem[];
}

export interface DemandItem {
    source: string;
    dest: string;
    algo: string;
    interfaceOut: string;
    mbps: number;
    gbps: number;
    pps: number;
    topoLink: string;
}

export interface DdmData {
    activeDemands: number;
    demands: DemandItem[];
}

export interface RecItem {
    id: string;
    rule: string;
    severity: string;
    resource: string;
    message: string;
    action: string;
    relatedView: string;
}

export interface RecsData {
    summary: Record<string, number>;
    recommendations: RecItem[];
}

// Chart palettes derived from the brand primitives in themes.css.
// Surf, turquoise, fuchsia, blue, green, yellow, red, plus lighter scale
// variants to keep lines visually distinct while staying on-brand.
export const ALGO_COLORS: Record<string, string> = {
    DEFAULT: '#00bcd1',     // brand-surf-500
    LATENCY: '#35ba72',     // primitive-green-500
    TE_METRIC: '#f6a400',   // primitive-yellow-500
    QRO_TUL_1: '#f3494f',   // primitive-red-500
    MXMEX_QRO_1: '#ff4089', // brand-fuchsia-500
};

export const PALETTE = [
    '#00bcd1', // brand-surf-500
    '#5ce5d5', // brand-turquoise-500
    '#ff4089', // brand-fuchsia-500
    '#3391ff', // primitive-blue-500
    '#35ba72', // primitive-green-500
    '#f6a400', // primitive-yellow-500
    '#f3494f', // primitive-red-500
    '#70dae5', // brand-surf-300
    '#ff77ab', // brand-fuchsia-300
    '#7f8a9e', // brand-cadet-400
];

export const ROUTER_COLORS = [
    '#00bcd1', // brand-surf-500
    '#35ba72', // primitive-green-500
    '#f6a400', // primitive-yellow-500
    '#f3494f', // primitive-red-500
    '#ff4089', // brand-fuchsia-500
    '#5ce5d5', // brand-turquoise-500
    '#3391ff', // primitive-blue-500
    '#70dae5', // brand-surf-300
    '#ff77ab', // brand-fuchsia-300
    '#7f8a9e', // brand-cadet-400
    '#a4e7ef', // brand-surf-200
    '#a0e3bf', // primitive-green-200
    '#fde6ba', // primitive-yellow-100
    '#faaeb0', // primitive-red-200
];

export const SEVERITY_BADGE: Record<string, string> = {
    high: 'bg-semantic-bg-danger-subtle text-semantic-text-danger',
    medium: 'bg-semantic-bg-warning-subtle text-semantic-text-warning',
    info: 'bg-semantic-bg-secondary text-semantic-text-secondary',
};

export function algoColor(name: string): string {
    if (ALGO_COLORS[name]) return ALGO_COLORS[name];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (name.codePointAt(i) ?? 0) + ((hash << 5) - hash);
    return PALETTE[Math.abs(hash) % PALETTE.length];
}

export function formatBytes(bytes: number): string {
    if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`;
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
    if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(0)} KB`;
    return `${bytes} B`;
}

export function mbpsColor(val: number): string {
    if (val > 800) return 'text-semantic-text-danger';
    if (val > 300) return 'text-semantic-text-warning';
    return 'text-semantic-text-success';
}
